import {CustomFieldType, RenderSeparator} from '@ee/common/enums';
import {Auditing} from './auditing.model';

export const OperatorList: Operator[] = ['==', '<=', '>=', '>', '<', 'in', 'none', 'blank', 'contains', 'not_contains', 'starts_with', 'ends_with'];
export type LogicalOperator = 'AND' | 'OR';
export type Operator = '==' | '<=' | '>=' | '>' | '<' | 'in' | 'none' | 'blank' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with';
export type ConditionParams = { field: string; opp: Operator; val: string | number | boolean };
export type Conditions = ConditionParams //| LogicalOperator | ConditionsList;// --> no nested rules and inferred AND

export type ConditionsList = Array<Conditions>;

export const fieldOptions: { name: string, description: string, icon: string, type: CustomFieldType }[] = [
  {
    name: 'Calculated',
    description: 'A calculated field based on an equation containing zero or more other fields.',
    icon: 'far fa-calculator',
    type: CustomFieldType.CALCULATED
  },
  {
    name: 'Offset Date',
    description: 'A date .',
    icon: 'far fa-calendar-days',
    type: CustomFieldType.DATE_OFFSET
  },
  {
    name: 'Boolean',
    description: 'A field used to represent a true or false value.',
    icon: 'far fa-toggle-on',
    type: CustomFieldType.BOOLEAN
  },
  {
    name: 'Checkboxes',
    description: 'Pick any number from a list of choices.',
    icon: 'far fa-list-check',
    type: CustomFieldType.MULTI_SELECT
  },
  {
    name: 'Radio Group',
    description: 'Pick exactly one from a list of choices.',
    icon: 'far fa-circle-dot',
    type: CustomFieldType.RADIO
  },
  {
    name: 'Date Input',
    description: 'A date input field.',
    icon: 'far fa-calendar-days',
    type: CustomFieldType.DATE
  },
  {
    name: 'Monetary',
    description: 'A currency amount. Turn off "Display as currency" for a plain number.',
    icon: 'far fa-input-numeric',
    type: CustomFieldType.DECIMAL
  },
  {
    name: 'Dropdown',
    description: 'Allows the client to select from a list of choices.',
    icon: 'far fa-list-dropdown',
    type: CustomFieldType.SELECT
  },
  {
    name: 'Text Input',
    description: 'A simple text input field.',
    icon: 'far fa-input-pipe',
    type: CustomFieldType.TEXT
  }
];

/**
 * A Past Due Rent History field — offered only in the case-template builder (not the attorney Fields
 * Settings), since it renders as a SurveyJS paneldynamic in the case form. A dynamic list (up to 10)
 * of {month, year, amount} rows whose amounts can sum into the case total.
 */
const RENT_HISTORY_FIELD_OPTION = {
  name: 'Past Due Rent History',
  description: 'A dynamic list (up to 10) of monthly past-due rent (month, year, amount); its amounts can sum into the case total.',
  icon: 'far fa-calendar-range',
  type: CustomFieldType.RENT_HISTORY
};

/** Icons for field types not offered in the (attorney) Add-field menu (so they still render an icon). */
const FALLBACK_FIELD_ICONS: Partial<Record<CustomFieldType, string>> = {
  [CustomFieldType.ARRAY]: 'far fa-users',        // subject collections (tenants, defendants…)
  [CustomFieldType.TAGS]: 'far fa-tags',           // multi-choice tags (e.g. Eviction Reason)
  [CustomFieldType.MULTI_TEXT]: 'far fa-align-left',
  [CustomFieldType.INTEGER]: 'far fa-input-numeric',
  [CustomFieldType.TIME]: 'far fa-clock',
  [CustomFieldType.FILE_UPLOAD]: 'far fa-file',
  [CustomFieldType.RENT_HISTORY]: RENT_HISTORY_FIELD_OPTION.icon  // case-only; not in the shared fieldOptions
};

export function fieldTypeToIcon(type: CustomFieldType): string {
  return fieldOptions.find(o => o.type === type)?.icon
    || FALLBACK_FIELD_ICONS[type]
    || 'far fa-input-pipe';
}

/**
 * A "smart" field is computed rather than directly entered — a calculated
 * (equation) field or an offset date. Shared by the attorney Fields Settings and
 * the case-template builder to split fields into Case vs Smart sections.
 */
export function isSmartFieldType(type: CustomFieldType): boolean {
  return type === CustomFieldType.CALCULATED || type === CustomFieldType.DATE_OFFSET;
}

/**
 * Field-type options shown under the "Case Fields" group of an Add-field menu. Includes the
 * case-only RENT_HISTORY type (rendered as a SurveyJS paneldynamic in the case form).
 */
export const caseFieldOptions = [
  ...fieldOptions.filter(o => !isSmartFieldType(o.type)),
  RENT_HISTORY_FIELD_OPTION
];

/**
 * Field-type options offered for a party/subject's own sub-fields.
 *
 * `MULTI_SELECT` is deliberately withheld: a subject's fields are edited through
 * `subject-entry-dialog`, which has no checkbox-group branch and would overwrite the stored array
 * with a String. Building that renderer is out of scope for the multi-select card — see
 * `.kiro/specs/intake-multi-select-custom-fields`. Filtered, never mutated: `caseFieldOptions` is
 * shared with the case-field menus, which DO offer the type.
 */
export const subjectFieldOptions = caseFieldOptions.filter(o => o.type !== CustomFieldType.MULTI_SELECT);

/** Field-type options shown under the "Smart Fields" group of an Add-field menu. */
export const smartFieldOptions = fieldOptions.filter(o => isSmartFieldType(o.type));

/**
 * One choice of a MULTI_SELECT or RADIO field. `value` is the Stored_Value — what lands on the case
 * and in generated documents; `label` is the Display_Label the user picks. Applies to those two types
 * only; every other choice type keeps label-equals-value.
 */
export interface CustomFieldOption {
  value: string;
  label: string;
}

/**
 * The authored choices of a MULTI_SELECT, from whichever carrier the field actually has: the
 * `option_definitions` rows, or — for a field carrying only bare `options` — one option per entry
 * whose label equals its value (Req 2.2). A blank Stored_Value means "defaults to the label"
 * (Req 4.6) and is resolved here, because two such rows would otherwise both store `''` and collide
 * on a template `track` expression.
 *
 * Shared deliberately. Every surface that renders a checkbox group needs exactly this resolution,
 * and hand-rolling it is why a MULTI_SELECT reached production rendering as a text box on two
 * surfaces. `ee-field`, `ee-custom-field` and the bulk-update dialog all resolve through here now;
 * resolve through it rather than adding another copy.
 *
 * One site deliberately does NOT use this: `case.model.ts`'s `fieldDefinitionToSurveyQuestion` emits
 * SurveyJS `{value, text}` for the PM canvas, a different output shape. It does not apply the Req 4.6
 * blank-value rule, which is a real divergence and tracked separately — mentioned here so the next
 * reader does not assume this function is already the single authority everywhere.
 *
 * Returns a new array; callers that append orphaned stored values must not mutate the field's own
 * `option_definitions`.
 */
export function resolveMultiSelectOptions(
  field: { option_definitions?: CustomFieldOption[]; options?: string[] } | null | undefined
): CustomFieldOption[] {
  if (field?.option_definitions?.length) {
    return field.option_definitions.map(o => ({ value: (o?.value ?? '').trim() || o?.label, label: o?.label }));
  }
  return (field?.options ?? []).map(value => ({ value, label: value }));
}

export interface BasicField {
  label: string;
  field?: string | null;
  type: CustomFieldType;
}

export interface CustomField extends BasicField, Auditing  {
  hint?: string;
  hint_more_info?: string;
  order?: number;
  columns?: number;
  required?: boolean;
  value?: string;
  link_id?: string; // generic field to link to any other resources
  options?: string[];
  /**
   * MULTI_SELECT and RADIO only — the {value, label} pairs behind `options`. Optional and additive:
   * when it is absent, each `options` entry is an option whose label equals its value (today's
   * behaviour for every choice field). `options` is always kept equal to the ordered list of these
   * values.
   */
  option_definitions?: CustomFieldOption[];
  /** MULTI_SELECT only — how selections join into one string in documents/reports. Null ⇒ COMMA. */
  render_separator?: RenderSeparator | null;
  /**
   * The value a control is pre-filled with when a form for a **new** record is constructed. Never
   * applied when a form is opened over saved data — a default that fired on load would overwrite a
   * blank the user deliberately left blank, invisibly, until the next unrelated save.
   *
   * Absent means "no default", and is distinct from `''`. For a DATE field it is one of the two
   * sentinels in `DATE_DEFAULT_*` or an ISO date; absent there means "inherit the org date-default
   * settings".
   */
  default_value?: any;
  /**
   * TEXT only — a raw `ngx-mask` pattern, or absent for an unrestricted input. Chosen from
   * `MASK_PRESETS`. Note this is **not** cosmetic: the app runs ngx-mask at its default
   * `dropSpecialCharacters: true`, so the mask characters are stripped from the stored value.
   */
  mask?: string | null;
  false_label?: string;
  true_label?: string;
  include_in_total?: boolean;
  /**
   * When set, overrides whether a numeric (DECIMAL) field renders as currency (`$`).
   * `null`/`undefined` ⇒ legacy default (DECIMAL is treated as monetary); `false` ⇒ plain number.
   */
  monetary?: boolean | null;
  prevent_removal?: boolean;
  hide_from_intake?: boolean;
  hide?: boolean;
  hide_from_client?: boolean;
  built_in_field?: boolean;
  description?: string;
  example?: string;
}
